import { Alert } from "@coval/ui"

import type { MetricCatalogItem } from "./metricCatalogItem"

interface MetricAvailabilityNoticeProps {
  metrics: MetricCatalogItem[]
  /** Reason per metric name, for the metrics this setup rules out. */
  metricErrors: Record<string, string>
}

/**
 * States up front how much of the catalog the current setup rules out. The rows
 * carry their own reason, but a reason on a row only reaches someone already
 * looking at that row — this counts them where the eye lands first, so an
 * unavailable metric is apparent before it is clicked.
 *
 * Renders nothing when every metric on offer can be picked.
 */
export function MetricAvailabilityNotice({
  metrics,
  metricErrors,
}: MetricAvailabilityNoticeProps) {
  // Counted over the metrics actually on offer: `metricErrors` can name a
  // metric the caller never passed, and that one has no row to speak for.
  const reasons = new Map<string, number>()
  metrics.forEach((metric) => {
    const reason = metricErrors[metric.metric_name]
    if (!reason) {
      return
    }
    reasons.set(reason, (reasons.get(reason) ?? 0) + 1)
  })

  if (reasons.size === 0) {
    return null
  }

  const total = Array.from(reasons.values()).reduce(
    (sum, count) => sum + count,
    0,
  )
  // Reasons are the callers' own strings, so a trailing period is stripped
  // rather than left to double up with the sentence's own.
  const summary = Array.from(reasons.keys())
    .map((reason) => reason.replace(/\.$/, ""))
    .join("; ")

  return (
    <div className="bg-surface-contrast px-6 py-3">
      <Alert variant="info">
        <span>
          <strong className="font-medium">
            {`${total} ${total === 1 ? "metric is" : "metrics are"} not available for this setup.`}
          </strong>{" "}
          {`${summary}. Unavailable metrics stay listed below, greyed out, and can't be selected.`}
        </span>
      </Alert>
    </div>
  )
}
